import {
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  IconArrowBoxRight,
  IconReload,
  LogoHp,
  Modal,
} from "@veneer/core";
import "../../assets/css/views/login.css";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import userService from "../../services/generalApi/user.service";
import { ISignInHpId } from "../../models/dto/generalApi/ISignInHpId";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { updateAuthentication } from "../../features/auth/authenticationSlice";
import { updateUser } from "../../features/auth/userSlice";
import { LoginEnum } from "../../models/enum/GeneralApi/LoginEnum";
import { encryptText } from "../../utils/encryptionUtils";
import { IUserDto } from "../../models/dto/generalApi/IUserDto";

export default function Login() {
  const [t, i18n] = useTranslation("global");
  const supportedLanguages = ["es", "en", "pt"];
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [open, setOpen] = useState(false);

  const [missingTermsAndCond, setMissingTermsAndCond] =
    useState<boolean>(false);
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    let language = navigator.language.substring(0, 2);
    let languageStored = localStorage.getItem("language");
    if (languageStored !== null) {
      i18n.changeLanguage(languageStored);
    } else {
      if (supportedLanguages.find((s) => s === language)) {
        i18n.changeLanguage(language);
      } else {
        alert(
          "Your language is not supported. Default English language selected"
        );
        i18n.changeLanguage("en");
      }
    }
  }, []);

  useEffect(() => {
    let code = searchParams.get("code");
    if (code) {
      let state = searchParams.get("state");
      if (state === sessionStorage.getItem("hppm-state")) {
        let signInHpId: ISignInHpId = {
          code,
          urlReturn: String(process.env.REACT_APP_URL_REDIRECT),
        };
        setLoading(true);
        userService
          .login(signInHpId)
          .then((r) => {
            if (r !== undefined) {
              if (r.userId === undefined) {
                showErrorMessage(LoginEnum.ServerError);
              } else if (r.userId !== 0) {
                localStorage.setItem("token", encryptText(r.token));
                saveUser(r);
                dispatch(updateAuthentication(true));
                navigate("/");
              } else {
                if (r.resultId)
                  if (r.resultId === LoginEnum.MissingTermsAndCond) {
                    setEmail(r.email);
                    setMissingTermsAndCond(true);
                  } else {
                    showErrorMessage(LoginEnum.ServerError);
                  }
              }
            } else {
              showErrorMessage(LoginEnum.NotFoundUser);
            }
          })
          .catch((e) => {
            showErrorMessage(LoginEnum.ServerError);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, []);

  const saveUser = (r: IUserDto) => {
    let user: IUserDto = {
      role: "",
      countryId: "",
      lastName: "",
      name: "",
      roleId: "",
      userId: "",
      refreshToken: "",
      isCheckWeb: "",
      resultId: "",
    };
    user.refreshToken = encryptText(r.refreshToken);
    user.userId = encryptText(r.userId.toString());
    user.countryId = encryptText(r.countryId.toString());
    user.lastName = encryptText(r.lastName);
    user.name = encryptText(r.name);
    user.roleId = encryptText(r.roleId.toString());
    user.role = encryptText(r.role);
    user.isCheckWeb = encryptText(r.isCheckWeb ? r.isCheckWeb.toString() : "");
    user.resultId = encryptText(r.resultId ? r.resultId.toString() : "");
    dispatch(updateUser(user));
  };

  const showErrorMessage = (loginType: number) => {
    if (loginType === LoginEnum.NotFoundUser)
      setErrorMessage(t("header.notFoundUser"));
    else if (loginType === LoginEnum.InactiveUser)
      setErrorMessage(t("header.inactiveUser"));
    else if (loginType === LoginEnum.DeletedUser)
      setErrorMessage(t("header.deletedUser"));
    else if (loginType === LoginEnum.MissingTermsAndCond)
      setErrorMessage(t("header.missingTermsAndCond"));
    else if (loginType === LoginEnum.TermsAcceptedSuccessfully)
      setErrorMessage(t("header.messageTermsAcceptedSuccessfully"));
    else if (loginType === LoginEnum.ErrorAcceptingTerms)
      setErrorMessage(t("header.messageErrorAcceptingTerms"));
    else setErrorMessage(t("header.serverError"));
    setOpen(true);
  };

  const closeHpIdSession = () => {
    let urlAuthProvide = String(process.env.REACT_APP_AUTH_PROVIDER);
    let urlRedirect = String(process.env.REACT_APP_URL_REDIRECT);
    let url = `${urlAuthProvide}oauth/logout?post_logout_redirect_uri=${urlRedirect}`;
    document.location.href = url;
  };

  const loginHpId = function () {
    setLoading(true);
    let guid = uuidv4();
    sessionStorage.setItem("hppm-state", guid);
    let urlAuthProvide = String(process.env.REACT_APP_AUTH_PROVIDER);
    let clientId = String(process.env.REACT_APP_AUTH_CLIENT);
    let urlRedirect = String(process.env.REACT_APP_URL_REDIRECT);
    let url = `${urlAuthProvide}oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${urlRedirect}&scope=user.profile.read&state=${guid}`;
    document.location.href = url;
  };

  const confirmTermsAndConditions = () => {
    try {
      userService
        .approvalTermsAndConditions(email)
        .then((r) => {
          setEmail("");
          setMissingTermsAndCond(false);
          if (r.result === true) {
            showErrorMessage(LoginEnum.TermsAcceptedSuccessfully);
          } else {
            showErrorMessage(LoginEnum.ErrorAcceptingTerms);
          }
        })
        .catch((e) => {
          setMissingTermsAndCond(false);
          setEmail("");
          console.error(e);
          closeHpIdSession();
        });
    } catch (error) {
      setMissingTermsAndCond(false);
      setEmail("");
      console.error(error);
      closeHpIdSession();
    }
  };

  const cancelTermsAndConditions = () => {
    setEmail("");
    setMissingTermsAndCond(false);
    closeHpIdSession();
  };

  return (
    <div>
      {/* <div className="splitLeft">
        <div className="centered">
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <LogoHp appearance="multicolor" size={144} />
          </div>
          <Card
            appearance="dropShadow"
            className="separation"
            content={
              <div>
                <div
                  className="center"
                  style={{ fontFamily: "Forma DJR Micro" }}
                >
                  <h6>{t("header.welcome")}</h6>
                  <h6 translate="no">
                    <b>HP POS MANAGER</b>
                  </h6>
                  <h6>{t("header.oldWeb")}</h6>
                </div>
                <div className="center">
                  <Button
                    onClick={() => {
                      window.open(
                        "https://posmanager.hpcloud.hp.com/posm/",
                        "_blank"
                      );
                    }}
                    style={{
                      fontFamily: "Forma DJR Micro",
                      width: "200px",
                      height: "54px",
                      marginBottom: "20px",
                      marginTop: "30px",
                    }}
                    leadingIcon={<IconArrowBoxRight />}
                  >
                    {t("header.sign_in")}
                  </Button>
                </div>
                <br />
                <div className="center">
                  {i18n.language === "es" && (
                    <div className="center">
                      <a
                        className="text"
                        href="https://www.hp.com/es-es/privacy/privacy.html"
                        target={"_blank"}
                        rel="noreferrer"
                      >
                        {t("header.HPPrivacy")}
                      </a>
                    </div>
                  )}
                  {i18n.language === "pt" && (
                    <div className="center">
                      <a
                        className="text"
                        href="https://www.hp.com/br-pt/privacy/privacy.html"
                        target={"_blank"}
                        rel="noreferrer"
                      >
                        {t("header.HPPrivacy")}
                      </a>
                    </div>
                  )}
                  {i18n.language === "en" && (
                    <div className="center">
                      <a
                        className="text"
                        href="https://www.hp.com/us-en/privacy/privacy.html"
                        target={"_blank"}
                        rel="noreferrer"
                      >
                        {t("header.HPPrivacy")}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            }
          />
          <Modal
            align="start"
            id="modal-id"
            show={open}
            footer={
              <ButtonGroup style={{ textAlign: "center" }}>
                <Button onClick={() => closeHpIdSession()}>
                  {t("header.confirm")}
                </Button>
              </ButtonGroup>
            }
            title={t("header.information")}
            children={<>{errorMessage}</>}
          />
        </div>
      </div> */}

      <div className="splitRight">
        <div className="centered">
          <div className="center">
            <Card
              appearance="dropShadow"
              className="separation"
              customStyle={{
                backgroundColor: "#F9F9F9",
              }}
              content={
                <div>
                  <img
                    src={require("../../assets/img/NEW_POS_LOGO.png")}
                    alt="HP POS Manager Logo"
                    style={{
                      width: "144px",
                      height: "144px",
                      borderRadius: "30px",
                    }}
                  />
                  <div
                    className="center"
                    style={{ fontFamily: "Forma DJR Micro" }}
                  >
                    <h6>{t("header.welcome")}</h6>
                    <h6 translate="no">
                      <b>HP POS MANAGER 2.0</b>
                    </h6>
                  </div>
                  <div className="center">
                    <Button
                      loading={loading}
                      onClick={loginHpId}
                      style={{
                        fontFamily: "Forma DJR Micro",
                        width: "200px",
                        height: "54px",
                        marginBottom: "20px",
                        marginTop: "30px",
                        backgroundColor: "#024AD8",
                        color: "#FFFFFF",
                      }}
                      leadingIcon={<IconArrowBoxRight />}
                    >
                      {t("header.sign_in")}
                    </Button>
                  </div>
                  <br />
                  <div className="center">
                    {i18n.language === "es" && (
                      <div className="center">
                        <a
                          className="text"
                          style={{
                            color: "#024AD8",
                            fontFamily: "Forma DJR Micro",
                          }}
                          href="https://www.hp.com/es-es/privacy/privacy.html"
                          target={"_blank"}
                          rel="noreferrer"
                        >
                          {t("header.HPPrivacy")}
                        </a>
                      </div>
                    )}
                    {i18n.language === "pt" && (
                      <div className="center">
                        <a
                          className="text"
                          style={{
                            color: "#024AD8",
                          }}
                          href="https://www.hp.com/br-pt/privacy/privacy.html"
                          target={"_blank"}
                          rel="noreferrer"
                        >
                          {t("header.HPPrivacy")}
                        </a>
                      </div>
                    )}
                    {i18n.language === "en" && (
                      <div className="center">
                        <a
                          className="text"
                          style={{
                            color: "#024AD8",
                          }}
                          href="https://www.hp.com/us-en/privacy/privacy.html"
                          target={"_blank"}
                          rel="noreferrer"
                        >
                          {t("header.HPPrivacy")}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </div>

      <div style={{ position: "relative" }}>
        <Modal
          className="modalTermsAndConditions"
          align="center"
          id="modalTermsAndConditions"
          show={missingTermsAndCond}
          footer={
            <ButtonGroup className="modalBrandsConfirmCreationButtonGroup">
              <Button onClick={cancelTermsAndConditions} appearance="secondary">
                {t("header.cancel")}
              </Button>
              <Button
                disabled={!acceptedTerms}
                onClick={confirmTermsAndConditions}
              >
                {t("header.accept")}
              </Button>
            </ButtonGroup>
          }
          title={t("header.termsOfService")}
        >
          <div className="modalTermsAndConditionsDiv">
            <p
              style={{ fontSize: "18px", marginBottom: "14px", color: "#333" }}
            >
              {t("header.lastRevision")}
            </p>

            <h4
              style={{
                fontSize: "20px",
                marginBottom: "16px",
                color: "#000",
                fontWeight: "800",
                textAlign: "justify",
              }}
            >
              {t("header.pleaseRead")}
            </h4>

            <p
              style={{
                marginBottom: "16px",
                fontSize: "16px",
                color: "#666",
                lineHeight: "1.5",
              }}
            >
              {t("header.agreement")}
              <strong>{t("header.HPPOSManager")}</strong>
              {t("header.agreement2")}
              <strong>{t("header.HPPOSManagerTeam")}</strong>
              {t("header.agreement3")}
              <strong>{t("header.HPPOSManagerTeam")}</strong>
              {t("header.agreement4")}
              <strong>{t("header.HPPOSManagerTeam")}</strong>
              {t("header.agreement5")}
              <strong>{t("header.HPInc")}</strong>
              {t("header.agreement6")}
            </p>

            <p
              style={{
                fontWeight: "800",
                marginBottom: "14px",
                fontSize: "16px",
                color: "#00000",
              }}
            >
              {t("header.privacy")}
            </p>

            <p style={{ fontSize: "16px", color: "#666", lineHeight: "1.5" }}>
              <strong>{t("header.HPPOSManagerTeam")}</strong>
              {t("header.agreement7")}
              <strong>{t("header.HPPOSManagerTeams")}</strong>
              {t("header.agreement8")}
              <strong>{t("header.HPPrivacy")}</strong>
              {t("header.agreement9")}
              <strong>{t("header.HPPOSManager")}</strong>
              {t("header.agreement10")}
              <strong>{t("header.HPInc")}</strong>
              {t("header.agreement11")}
            </p>

            <p
              style={{
                marginBottom: "30px",
                fontSize: "16px",
                color: "#666",
                lineHeight: "1.5",
              }}
            >
              {t("header.agreement12")}
            </p>

            <p
              style={{
                fontWeight: "800",
                marginBottom: "14px",
                fontSize: "16px",
                color: "#666",
              }}
            >
              {t("header.toBeBound")}
            </p>

            <p
              style={{
                marginBottom: "80px",
                fontSize: "16px",
                color: "#666",
                lineHeight: "1.5",
              }}
            >
              {t("header.agreement13")}
            </p>
          </div>

          <Checkbox
            value={acceptedTerms}
            checked={acceptedTerms}
            onChange={() => setAcceptedTerms(!acceptedTerms)}
            label={t("header.acceptTerms")}
          />
        </Modal>
      </div>
    </div>
  );
}
